import { useEffect, useMemo, useState, useCallback, useRef, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  fetchMe,
  fetchCompanies,
  fetchStatus,
  triggerRefresh,
  logout,
  logoutAll,
  exportUrl,
} from "../lib/api";
import {
  ArrowDown,
  ArrowUp,
  ArrowDownUp,
  Search,
  RefreshCw,
  LogOut,
  ShieldOff,
  Download,
  FileSpreadsheet,
  Building2,
  Calendar,
  Clock,
  GripVertical,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const DEFAULT_COLUMNS = [
  { key: "name", label: "Nom entreprise", mono: false },
  { key: "address", label: "Adresse", mono: false },
  { key: "postal_code", label: "Code postal", mono: true },
  { key: "city", label: "Ville", mono: false },
  { key: "siren", label: "SIREN", mono: true },
  { key: "siret", label: "SIRET", mono: true },
  { key: "legal_form_label", label: "Forme juridique", mono: false },
  { key: "naf_code", label: "Secteur NAF", mono: true },
  { key: "naf_label", label: "Libellé d'activité", mono: false },
  { key: "naf_section_label", label: "Nature de l'activité", mono: false },
  { key: "date_creation", label: "Date de création", mono: true },
];

const ORDER_STORAGE_KEY = "sirene_column_order_v3";
const WIDTH_STORAGE_KEY = "sirene_column_widths_v1";
const SELECT_COL_WIDTH = 40;
const MIN_COL_WIDTH = 70;
const MAX_COL_WIDTH = 800;
const DEFAULT_WIDTHS = {
  name: 220,
  address: 300,
  postal_code: 110,
  city: 170,
  siren: 120,
  siret: 150,
  legal_form_label: 220,
  naf_code: 110,
  naf_label: 280,
  naf_section_label: 220,
  date_creation: 140,
};

function loadOrder() {
  try {
    const raw = localStorage.getItem(ORDER_STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMNS.map((c) => c.key);
    const parsed = JSON.parse(raw);
    const valid = parsed.filter((k) => DEFAULT_COLUMNS.find((c) => c.key === k));
    const missing = DEFAULT_COLUMNS.map((c) => c.key).filter((k) => !valid.includes(k));
    return [...valid, ...missing];
  } catch {
    return DEFAULT_COLUMNS.map((c) => c.key);
  }
}

function saveOrder(order) {
  localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(order));
}

function loadWidths() {
  try {
    const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return { ...DEFAULT_WIDTHS, ...parsed };
  } catch {
    return { ...DEFAULT_WIDTHS };
  }
}

function saveWidths(widths) {
  localStorage.setItem(WIDTH_STORAGE_KEY, JSON.stringify(widths));
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `il y a ${diff}s`;
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return d.toLocaleString("fr-FR");
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState("date_creation");
  const [sortDir, setSortDir] = useState("desc");
  const [lookbackDays, setLookbackDays] = useState(30);
  const [status, setStatus] = useState({});
  const [columnOrder, setColumnOrder] = useState(loadOrder);
  const [colWidths, setColWidths] = useState(loadWidths);
  const [resizingKey, setResizingKey] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const dragKey = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  // Keep the top scroll bar inner div width in sync with the table's scrollWidth
  const tableScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const [innerWidth, setInnerWidth] = useState(0);

  // Row virtualization (for handling 5,000+ rows smoothly)
  const ROW_HEIGHT = 41;
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => tableScrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 15,
  });

  const columns = useMemo(
    () => columnOrder.map((k) => DEFAULT_COLUMNS.find((c) => c.key === k)).filter(Boolean),
    [columnOrder]
  );

  useEffect(() => {
    (async () => {
      try {
        const u = await fetchMe();
        setUser(u);
      } catch {
        navigate("/", { replace: true });
      }
    })();
  }, [navigate]);

  const dateRange = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - lookbackDays);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { date_from: fmt(from), date_to: fmt(to) };
  }, [lookbackDays]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCompanies({
        search: search || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
        limit: 20000,
        ...dateRange,
        _t: Date.now(),
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      // Drop any selected siret that no longer appears
      setSelected((prev) => {
        const next = new Set();
        const visible = new Set((data.items || []).map((c) => c.siret));
        for (const s of prev) if (visible.has(s)) next.add(s);
        return next;
      });
    } finally {
      setLoading(false);
    }
  }, [search, sortBy, sortDir, dateRange]);

  const loadStatus = useCallback(async () => {
    try {
      const s = await fetchStatus();
      setStatus(s);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    loadStatus();
  }, [user, load, loadStatus]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(
      () => {
        load();
        loadStatus();
      },
      30 * 60 * 1000
    );
    const statusInterval = setInterval(loadStatus, 30 * 1000);
    return () => {
      clearInterval(interval);
      clearInterval(statusInterval);
    };
  }, [user, load, loadStatus]);

  // Keep the top scroll bar inner div width in sync with the table's scrollWidth
  useLayoutEffect(() => {
    if (tableScrollRef.current) {
      setInnerWidth(tableScrollRef.current.scrollWidth);
    }
  }, [items, columns, loading]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    const t = toast.loading("Synchronisation INSEE en cours…");
    try {
      const result = await triggerRefresh(lookbackDays);
      await load();
      await loadStatus();
      const count = result?.count ?? 0;
      if (result?.ok) {
        toast.success(`${count.toLocaleString("fr-FR")} entreprises synchronisées`, { id: t });
      } else {
        toast.error(result?.error || "Erreur de synchronisation INSEE", { id: t });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la synchronisation", { id: t });
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate("/", { replace: true });
    }
  };

  const handleLogoutAll = async () => {
    if (
      !window.confirm(
        "Déconnecter TOUS les appareils ? Toutes les sessions actives (y compris celle-ci) seront fermées et devront se reconnecter."
      )
    ) {
      return;
    }
    const t = toast.loading("Déconnexion de tous les appareils…");
    try {
      const result = await logoutAll();
      toast.success(
        `${result?.revoked ?? 0} session(s) déconnectée(s)`,
        { id: t }
      );
      setTimeout(() => navigate("/", { replace: true }), 800);
    } catch {
      toast.error("Échec de la déconnexion globale", { id: t });
    }
  };

  const downloadExport = (fmt) => {
    const sirets = selected.size > 0 ? Array.from(selected).join(",") : undefined;
    const params = {
      fmt,
      search: search || "",
      sort_by: sortBy,
      sort_dir: sortDir,
      date_from: dateRange.date_from,
      date_to: dateRange.date_to,
    };
    if (sirets) params.sirets = sirets;
    window.location.href = exportUrl(params);
  };

  // Column drag-and-drop
  const onDragStart = (key) => (e) => {
    dragKey.current = key;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
  };
  const onDragOver = (key) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== key) setDragOver(key);
  };
  const onDragLeave = () => setDragOver(null);
  const onDrop = (targetKey) => (e) => {
    e.preventDefault();
    const src = dragKey.current;
    setDragOver(null);
    dragKey.current = null;
    if (!src || src === targetKey) return;
    const order = [...columnOrder];
    const srcIdx = order.indexOf(src);
    const tgtIdx = order.indexOf(targetKey);
    if (srcIdx < 0 || tgtIdx < 0) return;
    order.splice(srcIdx, 1);
    order.splice(tgtIdx, 0, src);
    setColumnOrder(order);
    saveOrder(order);
  };

  const resetColumnOrder = () => {
    const def = DEFAULT_COLUMNS.map((c) => c.key);
    setColumnOrder(def);
    saveOrder(def);
  };

  // Column resize
  const startResize = (key) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[key] ?? DEFAULT_WIDTHS[key] ?? 160;
    setResizingKey(key);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev) => {
      const next = Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, startW + (ev.clientX - startX)));
      setColWidths((w) => ({ ...w, [key]: next }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setResizingKey(null);
      setColWidths((w) => {
        saveWidths(w);
        return w;
      });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const resetColumnWidths = () => {
    setColWidths({ ...DEFAULT_WIDTHS });
    saveWidths({ ...DEFAULT_WIDTHS });
  };

  // Row selection
  const toggleRow = (siret) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(siret)) next.delete(siret);
      else next.add(siret);
      return next;
    });
  };
  const allVisibleSelected = items.length > 0 && items.every((r) => selected.has(r.siret));
  const someVisibleSelected = items.some((r) => selected.has(r.siret));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const r of items) next.delete(r.siret);
      } else {
        for (const r of items) next.add(r.siret);
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // Top scroll bar mirror
  const onTopScroll = (e) => {
    if (tableScrollRef.current) tableScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
  };
  const onTableScroll = (e) => {
    if (topScrollRef.current) topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
  };
  const scrollBy = (delta) => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: delta, behavior: "smooth" });
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Toaster position="top-right" richColors closeButton />

      <div className="strip flex items-center justify-between" data-testid="header-strip">
        <span>INSEE Sirene Live · {status.total_companies ?? "—"} ENTREPRISES INDEXÉES</span>
        <span data-testid="header-last-sync">
          Last sync: {status.last_success ? timeAgo(status.last_success) : "en attente"}
        </span>
      </div>

      <header className="border-b-2 border-[var(--ink)] bg-white">
        <div className="px-6 lg:px-10 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-[var(--ink)] text-white w-10 h-10 flex items-center justify-center">
              <Building2 size={20} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-xl leading-none">
                MaPage<span style={{ color: "#2563eb" }}>Entreprise</span>.fr
              </div>
              <div className="label text-[var(--muted)] mt-1">Tableau de bord interne</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user.picture && (
              <img src={user.picture} alt="" className="w-9 h-9 border-2 border-[var(--ink)] object-cover" />
            )}
            <div className="hidden md:block text-right">
              <div className="text-sm font-semibold leading-tight">{user.name}</div>
              <div className="text-xs font-mono text-[var(--muted)]">{user.email}</div>
            </div>
            <button type="button" className="btn btn-secondary" onClick={handleLogoutAll} data-testid="logout-all-button" title="Déconnecter tous les appareils">
              <ShieldOff size={14} /> <span className="hidden lg:inline">Tous les appareils</span>
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleLogout} data-testid="logout-button">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <section className="px-6 lg:px-10 py-6 border-b-2 border-[var(--ink)] bg-[var(--paper)]">
        <div className="grid lg:grid-cols-12 gap-4 items-end">
          <form onSubmit={handleSearch} className="lg:col-span-5">
            <label className="label block mb-2">Recherche (nom · secteur · ville)</label>
            <div className="flex">
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="input border-2 border-[var(--ink)]"
                placeholder="Ex: boulangerie, 47.11A, Marseille…"
                data-testid="search-input"
              />
              <button type="submit" className="btn btn-primary" data-testid="search-submit">
                <Search size={14} />
              </button>
            </div>
          </form>

          <div className="lg:col-span-3">
            <label className="label block mb-2 flex items-center gap-2">
              <Calendar size={11} /> Période (jours)
            </label>
            <select
              value={lookbackDays}
              onChange={(e) => setLookbackDays(parseInt(e.target.value, 10))}
              className="input border-2 border-[var(--ink)]"
              data-testid="lookback-select"
            >
              <option value={1}>1 jour</option>
              <option value={3}>3 jours</option>
              <option value={7}>7 jours</option>
              <option value={14}>14 jours</option>
              <option value={30}>30 jours</option>
              <option value={60}>60 jours</option>
              <option value={90}>90 jours</option>
            </select>
          </div>

          <div className="lg:col-span-4 flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-button"
            >
              {refreshing ? <span className="spinner" /> : <RefreshCw size={14} />}
              {refreshing ? "Synchronisation…" : "Synchroniser"}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => downloadExport("csv")}
              data-testid="export-csv-button"
            >
              <Download size={14} />
              CSV{selected.size > 0 ? ` (${selected.size})` : ""}
            </button>
            <button
              type="button"
              className="btn btn-green"
              onClick={() => downloadExport("xlsx")}
              data-testid="export-xlsx-button"
            >
              <FileSpreadsheet size={14} />
              Excel{selected.size > 0 ? ` (${selected.size})` : ""}
            </button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 items-center">
          <span className="pill" data-testid="stat-total">
            <Building2 size={11} /> {total.toLocaleString("fr-FR")} résultats
          </span>
          <span className="pill pill-light">
            <Calendar size={11} /> {dateRange.date_from} → {dateRange.date_to}
          </span>
          <span className="pill pill-light">
            <Clock size={11} /> Auto-refresh: {status.interval_minutes || 30} min
          </span>
          <span className="pill pill-light">Forme juridique : Entrepreneur individuel</span>
          <span className="pill pill-light">État : Actif</span>
          {selected.size > 0 && (
            <span className="pill pill-accent" data-testid="selected-pill">
              <CheckSquare size={11} /> {selected.size} sélectionnée(s)
              <button
                type="button"
                onClick={clearSelection}
                className="ml-1 underline opacity-90 hover:opacity-100"
                data-testid="clear-selection"
              >
                effacer
              </button>
            </span>
          )}
          {search && (
            <span className="pill pill-warning" data-testid="active-search">
              Recherche: «{search}»
            </span>
          )}
          <button
            type="button"
            className="ml-auto text-xs font-mono underline text-[var(--muted)] hover:text-[var(--ink)]"
            onClick={resetColumnOrder}
            data-testid="reset-columns-button"
          >
            Réinitialiser l'ordre des colonnes
          </button>
          <button
            type="button"
            className="text-xs font-mono underline text-[var(--muted)] hover:text-[var(--ink)]"
            onClick={resetColumnWidths}
            data-testid="reset-widths-button"
          >
            Réinitialiser les largeurs
          </button>
        </div>
      </section>

      <main className="px-6 lg:px-10 py-6 flex-1">
        {/* Top scroll mirror + scroll buttons */}
        <div className="flex items-center gap-2 mb-2" data-testid="scroll-controls">
          <button
            type="button"
            onClick={() => scrollBy(-320)}
            className="btn btn-secondary !p-2"
            data-testid="scroll-left"
            aria-label="Défiler à gauche"
          >
            <ChevronLeft size={14} />
          </button>
          <div
            className="top-scroll"
            ref={topScrollRef}
            onScroll={onTopScroll}
            data-testid="top-scrollbar"
          >
            <div style={{ width: innerWidth, height: 1 }} />
          </div>
          <button
            type="button"
            onClick={() => scrollBy(320)}
            className="btn btn-secondary !p-2"
            data-testid="scroll-right"
            aria-label="Défiler à droite"
          >
            <ChevronRight size={14} />
          </button>
        </div>

        <div
          className="scroll-x"
          ref={tableScrollRef}
          onScroll={onTableScroll}
          data-testid="companies-table-wrapper"
          style={{ maxHeight: "calc(100vh - 340px)", overflowY: "auto" }}
        >
          <table className="data-table data-table-resizable" data-testid="companies-table">
            <colgroup>
              <col style={{ width: SELECT_COL_WIDTH }} />
              {columns.map((col) => (
                <col
                  key={col.key}
                  style={{ width: colWidths[col.key] ?? DEFAULT_WIDTHS[col.key] ?? 160 }}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className="select-col" data-testid="th-select">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected;
                    }}
                    onChange={toggleAllVisible}
                    data-testid="select-all-checkbox"
                    aria-label="Tout sélectionner"
                  />
                </th>
                {columns.map((col) => {
                  const active = sortBy === col.key;
                  return (
                    <th
                      key={col.key}
                      draggable
                      onDragStart={onDragStart(col.key)}
                      onDragOver={onDragOver(col.key)}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop(col.key)}
                      onClick={() => handleSort(col.key)}
                      className={[
                        active ? "sort-active" : "",
                        dragOver === col.key ? "drag-over" : "",
                        resizingKey === col.key ? "resizing" : "",
                      ].join(" ")}
                      data-testid={`sort-${col.key}`}
                      data-column-key={col.key}
                    >
                      <span className="inline-flex items-center gap-1.5 th-label">
                        <GripVertical size={11} className="opacity-40" />
                        <span className="th-text">{col.label}</span>
                        {active ? (
                          sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                        ) : (
                          <ArrowDownUp size={11} className="opacity-40" />
                        )}
                      </span>
                      <span
                        className="col-resizer"
                        onMouseDown={startResize(col.key)}
                        onClick={(e) => e.stopPropagation()}
                        onDragStart={(e) => e.preventDefault()}
                        data-testid={`resize-${col.key}`}
                        aria-label={`Redimensionner ${col.label}`}
                        role="separator"
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center py-12">
                    <span className="spinner inline-block align-middle mr-2" />
                    Chargement des entreprises…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center py-16" data-testid="empty-state">
                    <div className="font-display text-2xl mb-2">Aucun résultat</div>
                    <div className="text-[var(--muted)] text-sm mb-6">
                      Essayez d'élargir la période ou de lancer une synchronisation.
                    </div>
                    <button type="button" className="btn btn-primary" onClick={handleRefresh}>
                      <RefreshCw size={14} /> Synchroniser maintenant
                    </button>
                  </td>
                </tr>
              )}
              {!loading &&
                items.length > 0 &&
                (() => {
                  const virtualRows = rowVirtualizer.getVirtualItems();
                  const totalHeight = rowVirtualizer.getTotalSize();
                  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
                  const paddingBottom =
                    virtualRows.length > 0
                      ? totalHeight - virtualRows[virtualRows.length - 1].end
                      : 0;
                  return (
                    <>
                      {paddingTop > 0 && (
                        <tr aria-hidden="true">
                          <td
                            colSpan={columns.length + 1}
                            style={{ height: paddingTop, padding: 0, border: 0 }}
                          />
                        </tr>
                      )}
                      {virtualRows.map((vr) => {
                        const i = vr.index;
                        const row = items[i];
                        const isSel = selected.has(row.siret);
                        return (
                          <tr
                            key={row.siret || i}
                            ref={rowVirtualizer.measureElement}
                            data-index={i}
                            data-testid={`row-${i}`}
                            className={isSel ? "row-selected" : ""}
                          >
                            <td className="select-col">
                              <input
                                type="checkbox"
                                checked={isSel}
                                onChange={() => toggleRow(row.siret)}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`row-checkbox-${i}`}
                                aria-label={`Sélectionner ${row.name}`}
                              />
                            </td>
                            {columns.map((col) => {
                              const value = row[col.key];
                              return (
                                <td key={col.key} className={col.mono ? "mono" : ""} title={value || ""}>
                                  <span className="cell-truncate">
                                    {value || <span className="text-[var(--hairline)]">—</span>}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                      {paddingBottom > 0 && (
                        <tr aria-hidden="true">
                          <td
                            colSpan={columns.length + 1}
                            style={{ height: paddingBottom, padding: 0, border: 0 }}
                          />
                        </tr>
                      )}
                    </>
                  );
                })()}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-xs font-mono text-[var(--muted)] flex justify-between flex-wrap gap-2">
          <span>
            Affichage: {items.length.toLocaleString("fr-FR")} / {total.toLocaleString("fr-FR")}
            {selected.size > 0 && (
              <span className="ml-3 text-[var(--ink)]">
                {selected.size.toLocaleString("fr-FR")} sélectionnée(s)
              </span>
            )}
          </span>
          <span>
            {status.last_error
              ? `⚠ Dernière erreur de sync: ${status.last_error.slice(0, 80)}`
              : `Dernière synchro: ${status.last_success ? new Date(status.last_success).toLocaleString("fr-FR") : "—"}`}
          </span>
        </div>
      </main>

      <footer className="border-t-2 border-[var(--ink)] bg-white px-6 lg:px-10 py-4 text-xs font-mono text-[var(--muted)] flex justify-between">
        <span>INSEE Sirene · API v3.11 · Entrepreneur individuel uniquement</span>
        <span>Restricted Internal Tool · {user.email}</span>
      </footer>
    </div>
  );
}
